import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';

import { Formik } from 'formik';

import { StyledColumn, StyledContainer, StyledRow } from '../common/LeaseStyles';

import {
  AntdSelect as Select,
  DatePicker,
  Divider,
  Switch,
  AntdAlert as Alert,
} from '@yuhuinc/ui-library/';

import { LeaseCreatorPageFooter } from '../common/LeaseCreatorPageFooter';
import { LeaseCreatorFormItem } from '../common/LeaseCreatorFormItem';
import { FormError } from '../common/FormError';
import { LeaseRejectedAlert } from '../common/LeaseRejectedAlert';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

import { getTranslatedString } from 'helpers/i18n';
import { leaseCreatorColors } from 'helpers/ColorHelper';

import { validateForm } from './LeaseDetailsFormValidator';

import {
  getUnits,
  getUnitTypes,
  getProperty,
  checkUnitAvailabilityForLease,
} from 'actions/landlordPropertiesActions';
import { getDocumentTemplatesForProperty } from 'actions/landlordApplicationsActions';
import {
  storeDocumentTemplatesByTypes,
  storeBaseDocumentTemplate,
  storeFixedAddendumsForLease,
  storeServicesAndUtilitiesForLease,
  storeCurrentRentableItemsForLease,
  storeConditionalAddendumsForLease,
} from '../store/actions';
import { media, sizes } from 'helpers/MediaHelper';
import { responsive } from 'helpers/StyleHelper';
import LeaseHeaderProgress from '../common/LeaseHeaderProgress';

const FORM_NAME = 'lease_details_page';

const StyledFormContainer = styled.div`
  background-color: white;
  width: 100%;
  margin-top: 24px;
  border: 1px solid ${leaseCreatorColors.FORM_BORDER};
  border-radius: 5px;
`;

const StyledForm = styled(VerticalFormWrapper)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-size: 24px;
  padding: 16px;
  max-width: 580px;
`;

const StyledPageHeader = styled.div`
  font-size: 24px;
  line-height: 32px;
  color: 38;
  font-weight: 400;
  margin-bottom: 21px;
  ${media.mobile`
    margin-bottom: 0;
  `};
`;

const StyledSwitch = styled(Switch)`
  border-radius: 16px !important;
  ${media.mobile`
    margin: 5px 0 !important;
  `};
`;

const SwitchSpacer = styled.div`
  margin-top: 37px;
  ${media.mobile`
    margin: 21px 0;
  `};
`;

const StyledRequiredText = styled.span`
  font-size: ${responsive.text.desktop};
  font-weight: 400;
  ${media.mobile`
    font-size: ${responsive.text.mobile};
  `};
`;

const StyledDivider = styled(Divider)`
  margin: 13px 0;
  ${media.mobile`
    margin: 11px 0 0;
  `};
`;

const LeaseDetailsPage = ({
  forward,
  currentPage,
  totalPages,
  properties,
  units,
  unitTypes,
  leaseFormValues,
  setLeaseFormValues,
  getUnits,
  getUnitTypes,
  leaseDetails,
  getDocumentTemplatesForProperty,
  documentTemplatesForProperty,
  handleCancellation,
  saveDraftLease,
  getProperty,
  checkUnitAvailabilityForLease,
  checkingUnitAvailability,
  storeDocumentTemplatesByTypes,
  availableDocumentTemplates,
  storeBaseDocumentTemplate,
  storeFixedAddendumsForLease,
  fixedAddendums,
  storeServicesAndUtilitiesForLease,
  storeCurrentRentableItemsForLease,
  storeConditionalAddendumsForLease,
  conditionalAddendums,
  latestLeaseReview,
}) => {
  const [unitAvailabilityError, setUnitAvailabilityError] = useState([]);

  const isMobile = window.matchMedia(`(max-width: ${sizes.mobile}px)`).matches;
  const labelSize = isMobile ? responsive.text.mobile : responsive.text.desktop;

  const onBaseDocumentTemplateChange = useCallback(
    (newBaseDocumentTemplateId) => {
      const selectedDocumentTemplate = availableDocumentTemplates.find(
        (documentTemplate) => documentTemplate.value === newBaseDocumentTemplateId
      );
      const originalDocumentTemplate = availableDocumentTemplates.find(
        (documentTemplate) => documentTemplate.value === leaseFormValues.document_template_id
      );
      storeBaseDocumentTemplate(selectedDocumentTemplate);
      storeFixedAddendumsForLease(selectedDocumentTemplate, fixedAddendums);
      storeServicesAndUtilitiesForLease(
        originalDocumentTemplate,
        selectedDocumentTemplate,
        leaseFormValues
      );
    },
    [
      availableDocumentTemplates,
      storeBaseDocumentTemplate,
      fixedAddendums,
      storeFixedAddendumsForLease,
      leaseFormValues,
      storeServicesAndUtilitiesForLease,
    ]
  );

  const storeDocumentTemplatesForPropertyByTypes = useCallback(
    (documentTemplates) => {
      storeDocumentTemplatesByTypes(documentTemplates);
    },
    [storeDocumentTemplatesByTypes]
  );

  useEffect(() => {
    storeDocumentTemplatesForPropertyByTypes(documentTemplatesForProperty);
  }, [documentTemplatesForProperty, storeDocumentTemplatesForPropertyByTypes]);

  useEffect(() => {
    const rentableItemsStore = storeCurrentRentableItemsForLease(leaseFormValues.rentable_items);
    storeConditionalAddendumsForLease(
      rentableItemsStore.currentRentableItemsForLease,
      conditionalAddendums
    );
  }, [
    leaseFormValues.rentable_items,
    storeCurrentRentableItemsForLease,
    conditionalAddendums,
    storeConditionalAddendumsForLease,
  ]);

  const saveAndContinue = (values) => {
    onBaseDocumentTemplateChange(values.document_template_id);
    setLeaseFormValues((leaseFormValues) => ({
      ...leaseFormValues,
      ...values,
    }));
    saveDraftLease(values);
    forward();
  };

  return (
    <StyledContainer>
      <LeaseHeaderProgress lease={leaseDetails} stepsFilled={currentPage} totalSteps={totalPages} />

      <Formik
        initialValues={{
          lease_start_date:
            leaseFormValues.lease_start_date || leaseDetails.lease_start_date || undefined,
          lease_end_date:
            leaseFormValues.lease_end_date || leaseDetails.lease_end_date || undefined,
          early_move_in_date_toggle:
            !!leaseFormValues.early_move_in_date || !!leaseDetails.early_move_in_date || false,
          early_move_in_date:
            leaseFormValues.early_move_in_date || leaseDetails.early_move_in_date || undefined,
          prorate_rent: leaseFormValues.prorate_rent || leaseDetails.prorate_rent || false,
          property_id: leaseFormValues.property_id || leaseDetails.property.value || undefined,
          unit_id: leaseFormValues.unit_id || leaseDetails.unit_id || undefined,
          unit_type_id:
            leaseFormValues.unit_type_id || leaseDetails.unit_type_selected.value || undefined,
          delay_unit_assignment:
            leaseFormValues.delay_unit_assignment || leaseDetails.delay_unit_assignment || false,
          document_template_id: leaseFormValues.document_template_id || undefined,
        }}
        validateOnChange={true}
        validate={validateForm}
        onSubmit={async (values) => {
          setUnitAvailabilityError([]);
          if (values.delay_unit_assignment) {
            saveAndContinue(values);
            return;
          }
          const response = await checkUnitAvailabilityForLease({
            propertyId: values.property_id,
            unitId: values.unit_id,
            leaseStartDate: values.lease_start_date,
            leaseEndDate: values.lease_end_date,
          });
          if (response.action.payload.available) {
            saveAndContinue(values);
          } else {
            setUnitAvailabilityError(response.action.payload);
          }
        }}
      >
        {({ handleSubmit, values, touched, errors, setFieldValue, setFieldTouched }) => {
          // get applicant desired unit type verbiage
          const desiredUnitTypeVerbiage = getTranslatedString(
            `standardUnitTypes.${leaseDetails.unit_type_selected.type_code}`
          );

          const onPropertyChange = (propertyId) => {
            setFieldValue('property_id', propertyId);
            // reset unit, unit type, doc template values and fetch for new selected property
            setFieldTouched('unit_id');
            setFieldTouched('unit_type_id');
            setFieldTouched('document_template_id');
            setFieldValue('unit_id', null);
            setFieldValue('unit_type_id', null);
            setFieldValue('document_template_id', undefined);
            getUnits(propertyId, { for_multiselect: true });
            getUnitTypes(propertyId);
            getDocumentTemplatesForProperty(propertyId);
            getProperty(propertyId);
          };

          return (
            <StyledFormContainer>
              <StyledForm id={FORM_NAME} onSubmit={handleSubmit}>
                {/* Lease Terms Section */}
                <StyledPageHeader>
                  {getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leaseDetailsPage.leaseDetails'
                  )}
                </StyledPageHeader>
                {latestLeaseReview?.status === 'rejected' && (
                  <StyledRow gutter={16}>
                    <LeaseRejectedAlert latestLeaseReview={latestLeaseReview} />
                  </StyledRow>
                )}
                {!isEmpty(errors) && (
                  <StyledRow gutter={16}>
                    <FormError errors={errors} />
                  </StyledRow>
                )}
                {!isEmpty(unitAvailabilityError) && !unitAvailabilityError.available && (
                  <StyledRow gutter={16}>
                    <Alert
                      type="error"
                      message={unitAvailabilityError.occupying_leases.map((occupying_lease) => (
                        <div>{`Unit is occupied by ${occupying_lease.cotenants} from ${occupying_lease.start_date} to ${occupying_lease.end_date}`}</div>
                      ))}
                      closable={false}
                      showIcon
                    />
                  </StyledRow>
                )}
                <StyledRow gutter={16}>
                  <StyledColumn md={12}>
                    <LeaseCreatorFormItem
                      label={getTranslatedString(
                        'landlordPortal.leaseCreatorV2.leaseDetailsPage.startDate'
                      )}
                      labelSize={labelSize}
                      required
                    >
                      <DatePicker
                        id="lease_start_date"
                        name="lease_start_date"
                        placeholder={getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leaseDetailsPage.selectADate'
                        )}
                        onChange={(date) => {
                          setFieldTouched('lease_start_date', true);
                          if (date) {
                            setFieldValue('lease_start_date', date.format('YYYY-MM-DD'));
                          } else {
                            setFieldValue('lease_start_date', '');
                          }
                        }}
                        value={
                          values['lease_start_date']
                            ? moment(values['lease_start_date'])
                            : undefined
                        }
                        isMobile={isMobile}
                        error={touched.lease_start_date && errors.lease_start_date}
                      />
                    </LeaseCreatorFormItem>
                  </StyledColumn>
                  <StyledColumn md={12}>
                    <LeaseCreatorFormItem
                      label={getTranslatedString(
                        'landlordPortal.leaseCreatorV2.leaseDetailsPage.endDate'
                      )}
                      labelSize={labelSize}
                      required
                    >
                      <DatePicker
                        id="lease_end_date"
                        name="lease_end_date"
                        placeholder={getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leaseDetailsPage.selectADate'
                        )}
                        onChange={(date) => {
                          setFieldTouched('lease_end_date', true);
                          if (date) {
                            setFieldValue('lease_end_date', date.format('YYYY-MM-DD'));
                          } else {
                            setFieldValue('lease_end_date', '');
                          }
                        }}
                        value={
                          values['lease_end_date'] ? moment(values['lease_end_date']) : undefined
                        }
                        isMobile={isMobile}
                        error={touched.lease_end_date && errors.lease_end_date}
                      />
                    </LeaseCreatorFormItem>
                  </StyledColumn>
                </StyledRow>

                {/* Early move in toggle */}
                <StyledRow gutter={16}>
                  <StyledColumn md={24}>
                    <StyledSwitch
                      id="early_move_in_date_toggle"
                      label={getTranslatedString(
                        'landlordPortal.leaseCreatorV2.leaseDetailsPage.earlyMoveIn'
                      )}
                      labelSize={labelSize}
                      checked={values['early_move_in_date_toggle']}
                      onChange={(value) => {
                        setFieldValue('early_move_in_date_toggle', value);
                        setFieldValue('early_move_in_date', '');
                        setFieldValue('prorate_rent', false);
                      }}
                      isMobile={isMobile}
                    />
                  </StyledColumn>
                </StyledRow>

                {/* Early move in date and prorate toggle */}
                <StyledRow gutter={16}>
                  <StyledColumn md={16}>
                    <LeaseCreatorFormItem
                      label={getTranslatedString(
                        'landlordPortal.leaseCreatorV2.leaseDetailsPage.earlyMoveInDate'
                      )}
                      labelSize={labelSize}
                      required={values['early_move_in_date_toggle']}
                    >
                      <DatePicker
                        id="early_move_in_date"
                        name="early_move_in_date"
                        placeholder={getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leaseDetailsPage.selectADate'
                        )}
                        disabled={!values['early_move_in_date_toggle']}
                        onChange={(date) =>
                          date
                            ? setFieldValue('early_move_in_date', date.format('YYYY-MM-DD'))
                            : setFieldValue('early_move_in_date', '')
                        }
                        value={
                          values['early_move_in_date']
                            ? moment(values['early_move_in_date'])
                            : undefined
                        }
                        isMobile={isMobile}
                        error={touched.early_move_in_date && errors.early_move_in_date}
                      />
                    </LeaseCreatorFormItem>
                  </StyledColumn>
                  <StyledColumn md={8}>
                    <SwitchSpacer>
                      <StyledSwitch
                        id="prorate_rent"
                        name="prorate_rent"
                        label={getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leaseDetailsPage.prorateRent'
                        )}
                        labelSize={labelSize}
                        disabled={!values['early_move_in_date_toggle']}
                        checked={values['prorate_rent']}
                        onChange={(value) => setFieldValue('prorate_rent', value)}
                        isMobile={isMobile}
                      />
                    </SwitchSpacer>
                  </StyledColumn>
                </StyledRow>

                <StyledDivider dashed={false} plain={false} orientation="" type="horizontal" />

                {/* Property and Unit Section */}
                <StyledRow gutter={16} mt="16px">
                  <StyledColumn md={24}>
                    <LeaseCreatorFormItem
                      label={`${getTranslatedString(
                        'landlordPortal.leaseCreatorV2.leaseDetailsPage.property'
                      )}`}
                      labelSize={labelSize}
                      required
                    >
                      <Select
                        id="property_id"
                        name="property_id"
                        placeholder={getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leaseDetailsPage.selectAProperty'
                        )}
                        optionData={properties}
                        handleChange={onPropertyChange}
                        value={values['property_id']}
                        error={touched.property_id && errors.property_id}
                        isMobile={isMobile}
                      />
                    </LeaseCreatorFormItem>
                  </StyledColumn>
                </StyledRow>
                <StyledRow gutter={16}>
                  <StyledColumn md={16}>
                    {values.delay_unit_assignment ? (
                      <LeaseCreatorFormItem
                        label={`${getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leaseDetailsPage.unitType'
                        )}`}
                        labelSize={labelSize}
                        required
                        infoMessage={
                          leaseDetails.unit_type_selected.type_code &&
                          getTranslatedString(
                            'landlordPortal.leaseCreatorV2.leaseDetailsPage.hasRequestUnit',
                            { number_of_beds: desiredUnitTypeVerbiage }
                          )
                        }
                      >
                        <Select
                          id="unit_type_id"
                          name="unit_type_id"
                          placeholder={getTranslatedString(
                            'landlordPortal.leaseCreatorV2.leaseDetailsPage.selectAUnitType'
                          )}
                          optionData={unitTypes}
                          handleChange={(value) => {
                            setFieldTouched('unit_type_id', true);
                            setFieldValue('unit_type_id', value);
                          }}
                          value={values['unit_type_id']}
                          isMobile={isMobile}
                          error={touched.unit_type_id && errors.unit_type_id}
                        />
                      </LeaseCreatorFormItem>
                    ) : (
                      <LeaseCreatorFormItem
                        label={`${getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leaseDetailsPage.unit'
                        )}`}
                        labelSize={labelSize}
                        required
                        infoMessage={
                          leaseDetails.unit_type_selected.type_code &&
                          getTranslatedString(
                            'landlordPortal.leaseCreatorV2.leaseDetailsPage.hasRequestUnit',
                            { number_of_beds: desiredUnitTypeVerbiage }
                          )
                        }
                      >
                        <Select
                          id="unit_id"
                          name="unit_id"
                          placeholder={getTranslatedString(
                            'landlordPortal.leaseCreatorV2.leaseDetailsPage.selectAUnit'
                          )}
                          optionData={units}
                          handleChange={(value) => {
                            setFieldTouched('unit_id', true);
                            setFieldValue('unit_id', value);
                          }}
                          value={values['unit_id']}
                          isMobile={isMobile}
                          error={touched.unit_id && errors.unit_id}
                        />
                      </LeaseCreatorFormItem>
                    )}
                  </StyledColumn>
                  <StyledColumn md={8}>
                    <SwitchSpacer>
                      <StyledSwitch
                        id="delay_unit_assignment"
                        name="delay_unit_assignment"
                        label={getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leaseDetailsPage.assignUnitLater'
                        )}
                        labelSize={labelSize}
                        checked={values['delay_unit_assignment']}
                        onChange={(value) => {
                          setFieldValue('delay_unit_assignment', value);
                          setFieldTouched('unit_id');
                          setFieldTouched('unit_type_id');
                          setFieldValue('unit_id', null);
                          setFieldValue('unit_type_id', null);
                        }}
                        isMobile={isMobile}
                      />
                    </SwitchSpacer>
                  </StyledColumn>
                </StyledRow>

                {/* Lease template section */}
                <StyledRow gutter={16}>
                  <StyledColumn md={24}>
                    <LeaseCreatorFormItem
                      label={`${getTranslatedString(
                        'landlordPortal.leaseCreatorV2.leaseDetailsPage.baseLeaseTemplate'
                      )}`}
                      labelSize={labelSize}
                      required
                    >
                      <Select
                        id="document_template_id"
                        name="document_template_id"
                        placeholder={getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leaseDetailsPage.selectABaseLeaseTemplate'
                        )}
                        optionData={availableDocumentTemplates}
                        handleChange={(value) => setFieldValue('document_template_id', value)}
                        value={values['document_template_id']}
                        isMobile={isMobile}
                        error={touched.document_template_id && errors.document_template_id}
                      />
                    </LeaseCreatorFormItem>
                  </StyledColumn>
                </StyledRow>
                <StyledRow mb="0px">
                  <StyledColumn md={24}>
                    <LeaseCreatorFormItem
                      label={
                        <StyledRequiredText>{getTranslatedString('required')}</StyledRequiredText>
                      }
                      labelSize={labelSize}
                      required
                    />
                  </StyledColumn>
                </StyledRow>
              </StyledForm>
            </StyledFormContainer>
          );
        }}
      </Formik>
      <LeaseCreatorPageFooter
        currentPage={currentPage}
        totalPages={totalPages}
        formName={FORM_NAME}
        handleCancellation={handleCancellation}
        submitLoading={checkingUnitAvailability}
      />
    </StyledContainer>
  );
};

const mapStoreToProps = (store) => ({
  properties: store.landlordProperties.properties,
  units: store.landlordProperties.units,
  unitTypes: store.landlordProperties.unitTypes,
  documentTemplatesForProperty: store.landlordApplications.documentTemplatesForProperty,
  checkingUnitAvailability: store.landlordProperties.checkingUnitAvailability,
  availableDocumentTemplates: store.landlord.leaseCreatorV2.availableDocumentTemplates,
  fixedAddendums: store.landlord.leaseCreatorV2.fixedAddendums,
  conditionalAddendums: store.landlord.leaseCreatorV2.conditionalAddendums,
  latestLeaseReview: store.landlord.leaseCreatorV2.latestLeaseReview,
});

const mapDispatchToProps = {
  getUnits,
  getUnitTypes,
  getDocumentTemplatesForProperty,
  getProperty,
  checkUnitAvailabilityForLease,
  storeDocumentTemplatesByTypes,
  storeBaseDocumentTemplate,
  storeFixedAddendumsForLease,
  storeServicesAndUtilitiesForLease,
  storeCurrentRentableItemsForLease,
  storeConditionalAddendumsForLease,
};

export default connect(mapStoreToProps, mapDispatchToProps)(LeaseDetailsPage);
